import { useState } from "react";
import GrowthForm from "../../components/forms/growthForm";
import TemperatureForm from "../../components/forms/temperatureForm";
import MedicationForm from "../../components/forms/medicationForm";
import IllnessForm from "../../components/forms/illnessForm";
import HealthTable from "../../components/tables/healthTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { exportToCSV, growthHealthCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";

const GrowthHealth = () => {
  const { selectedChild } = useChild();
  const { items: healthData, addItem, updateItem, deleteItem, loading, error } = useFirestore("growthHealth", selectedChild?.id);
  const [activeTab, setActiveTab] = useState("growth");

  const handleAddHealthData = async (data) => {
    try {
      await addItem({ ...data, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding health data:", err);
    }
  };

  const handleExport = () => {
    exportToCSV(healthData, `growth-health-${selectedChild?.name || "export"}.csv`, growthHealthCSVColumns);
  };

  const handleExportPDF = () => {
    exportSectionPDF(healthData, `growth-health-${selectedChild?.name || "export"}.pdf`, growthHealthCSVColumns, "Growth & Health", selectedChild?.name || "");
  };

  const tabs =[
    { id: "growth", label: "📏 Growth", form: GrowthForm },
    { id: "temperature", label: "🌡️ Temperature", form: TemperatureForm },
    { id: "medication", label: "💊 Medication", form: MedicationForm },
    { id: "illness", label: "🤒 Illness", form: IllnessForm }
  ];

  return (
    <div className="card h-100" id="growth-health">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">📊 Growth & Health</h5>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-3">
          {tabs.map(tab => (
            <li className="nav-item" key={tab.id}>
              <button
                className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Active Form */}
        <div className="tab-content">
          {tabs.map(tab => {
            const FormComponent = tab.form;
            return (
              <div
                key={tab.id}
                className={`tab-pane ${activeTab === tab.id ? 'active' : ''}`}
              >
                <FormComponent onAdd={handleAddHealthData} />
              </div>
            );
          })}
        </div>

        {/* Health Records Table */}
        <div className="mt-4">
          <h6>Health Records</h6>
          {loading ? (
            <LoadingState message="Loading health records..." />
          ) : healthData.length > 0 ? (
            <HealthTable data={healthData} onUpdate={updateItem} onDelete={deleteItem} />
          ) : (
            <EmptyState
              icon="📏"
              heading="No measurements yet"
              message="Add your first height or weight reading to start tracking growth."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GrowthHealth;
